import React, { useState } from "react";
import { Download, Share2, Smartphone, X, Menu, Plus } from "lucide-react";
import { useInstall } from "@/context/InstallContext";
import { toast } from "sonner";

/**
 * Always-visible install button.
 * Uses the shared InstallContext so ALL install buttons share the single
 * `beforeinstallprompt` event. Never falls back to "just show modal" on
 * Android when the event is available — always calls `promptInstall()` first.
 */
export default function InstallPrompt({ variant = "floating", className = "" }) {
  const { canPrompt, promptInstall, platform, isStandalone, installed } = useInstall();
  const [showInstructions, setShowInstructions] = useState(false);

  const handleInstall = async () => {
    // Path 1 — native prompt available (Android Chrome/Edge, desktop Chrome).
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        toast.success("App ORIO in installazione…", { duration: 3000 });
        return;
      }
      if (outcome === "redirected") {
        // Android → Play Store deep link. Page is already navigating away.
        return;
      }
      if (outcome === "dismissed") {
        // Fall through to instructions to help the user retry via the menu.
        setShowInstructions(true);
        return;
      }
      // "unavailable" → show instructions.
      setShowInstructions(true);
      return;
    }
    // Path 2 — no native event. Show platform-specific instructions.
    setShowInstructions(true);
  };

  if (isStandalone || installed) return null;

  if (variant === "inline") {
    return (
      <>
        <button
          data-testid="install-pwa-btn-inline"
          onClick={handleInstall}
          className={`orio-cta-btn relative px-5 py-3 rounded-full text-black font-semibold inline-flex items-center justify-center gap-2 overflow-hidden ${className}`}
        >
          <span className="relative z-10 inline-flex items-center gap-2">
            <Download className="h-4 w-4" /> Installa l&apos;app
          </span>
        </button>
        {showInstructions && (
          <InstallInstructionsModal platform={platform} onClose={() => setShowInstructions(false)} />
        )}
      </>
    );
  }

  if (variant === "inline-mobile") {
    return (
      <>
        <button
          data-testid="install-pwa-btn-mobile"
          onClick={handleInstall}
          className={`orio-cta-btn relative w-full px-6 py-4 rounded-full text-black font-semibold inline-flex items-center justify-center gap-2 overflow-hidden shadow-xl ${className}`}
        >
          <span className="relative z-10 inline-flex items-center gap-2.5 text-base">
            <Download className="h-5 w-5" /> Installa l&apos;app ORIO
          </span>
        </button>
        {showInstructions && (
          <InstallInstructionsModal platform={platform} onClose={() => setShowInstructions(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <div className={`hidden md:block fixed left-6 bottom-6 z-[800] max-w-sm ${className}`}
        data-testid="install-pwa-floater">
        <div
          className="rounded-2xl p-3 flex items-center gap-3 border"
          style={{
            background: "#FFFFFF",
            borderColor: "#F0EBE3",
            boxShadow: "0 24px 48px -18px rgba(0,0,0,0.22), 0 6px 14px -6px rgba(0,0,0,0.08)",
          }}
        >
          <div className="h-10 w-10 rounded-xl shrink-0 flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
              boxShadow: "0 8px 18px -6px rgba(255,92,42,0.55)",
            }}>
            <Smartphone className="h-5 w-5 text-white" strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-[#1A1A1A]">Installa ORIO</div>
            <div className="text-[11px] text-[#7A7A7A]">Aggiungi alla Home — pesa zero</div>
          </div>
          <button data-testid="install-pwa-btn" onClick={handleInstall}
            className="orio-cta-btn relative px-3.5 py-2 rounded-full text-black text-[12px] font-semibold overflow-hidden">
            <span className="relative z-10 inline-flex items-center gap-1">
              <Download className="h-3.5 w-3.5" />Installa
            </span>
          </button>
        </div>
      </div>
      {showInstructions && (
        <InstallInstructionsModal platform={platform} onClose={() => setShowInstructions(false)} />
      )}
    </>
  );
}

/* ============================================================================
   Instructions Modal — shown only when the native prompt is not available.
   Detects platform automatically and shows the shortest possible path:
   • iOS Safari  → Condividi → Aggiungi a Home
   • Android     → menu (⋮) → "Installa app" / "Aggiungi a Home"
   • Desktop     → menu → Installa
============================================================================ */
export function InstallInstructionsModal({ platform, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[1600] flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(28,20,10,0.55)", backdropFilter: "blur(8px)" }}
      data-testid="install-instructions-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl p-5 sm:p-6 relative border"
        style={{
          background: "#FBF7F1",
          borderColor: "#F0EBE3",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="install-instructions-close-x"
          className="absolute right-3 top-3 h-10 w-10 rounded-full flex items-center justify-center text-[#9A9A9A] hover:text-[#1A1A1A] hover:bg-[#F4EFE6] transition-colors"
          aria-label="Chiudi"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A]">Installazione</div>
        <h3 className="font-display text-[26px] leading-[1.15] tracking-tight mt-1 text-[#1A1A1A]">
          {platform === "ios" ? (
            <>Aggiungi ORIO alla <span className="orio-grad-text">Home</span></>
          ) : platform === "android" ? (
            <>Installa ORIO come <span className="orio-grad-text">app nativa</span></>
          ) : (
            <>Installa ORIO nella <span className="orio-grad-text">barra app</span></>
          )}
        </h3>

        {platform === "ios" ? (
          <ol className="mt-5 space-y-2.5">
            <Step n={1} icon={Share2}>
              Tocca l&apos;icona <b>Condividi</b> nella barra bassa di Safari.
            </Step>
            <Step n={2} icon={Plus}>
              Scorri in basso e tocca <b>&laquo;Aggiungi a Home&raquo;</b>.
            </Step>
            <Step n={3} icon={Smartphone}>
              Conferma. ORIO compare come app — anche offline, con notifiche.
            </Step>
          </ol>
        ) : platform === "android" ? (
          <ol className="mt-5 space-y-2.5">
            <Step n={1} icon={Menu}>
              Tocca i <b>tre puntini ⋮</b> in alto a destra del browser.
            </Step>
            <Step n={2} icon={Download}>
              Scegli <b>&laquo;Installa app&raquo;</b> o <b>&laquo;Aggiungi a schermata Home&raquo;</b>.
            </Step>
            <Step n={3} icon={Smartphone}>
              Conferma. ORIO comparirà nel drawer come una qualsiasi app.
            </Step>
          </ol>
        ) : (
          <ol className="mt-5 space-y-2.5">
            <Step n={1} icon={Menu}>
              Nel browser apri il menù (⋮ o ≡) in alto a destra.
            </Step>
            <Step n={2} icon={Download}>
              Clicca <b>&laquo;Installa ORIO&raquo;</b> o <b>&laquo;Installa app&raquo;</b>.
            </Step>
            <Step n={3} icon={Smartphone}>
              Conferma nella finestra del sistema.
            </Step>
          </ol>
        )}

        {platform === "android" && (
          <div className="mt-4 rounded-2xl border border-[#F0EBE3] bg-white p-3 text-[11.5px] text-[#5C5C5C] leading-relaxed">
            <b className="text-[#1A1A1A]">Suggerimento:</b> se il tuo browser non mostra
            l&apos;opzione, potresti aver già dismesso l&apos;invito. Chiudi e riapri il browser,
            oppure prova da <b>Chrome</b>: è il modo più veloce.
          </div>
        )}

        <button
          onClick={onClose}
          data-testid="install-instructions-close"
          className="mt-5 w-full orio-cta-btn relative px-5 py-3.5 rounded-full text-black font-semibold overflow-hidden"
        >
          <span className="relative z-10">Ho capito, lo provo ora</span>
        </button>
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, children }) {
  return (
    <li className="flex items-start gap-3 rounded-2xl p-3 border" style={{ borderColor: "#F0EBE3", background: "#FFFFFF" }}>
      <span
        className="h-7 w-7 rounded-full text-white text-[12px] font-bold flex items-center justify-center shrink-0"
        style={{ background: "linear-gradient(135deg,#FF7A1A,#FF5C2A)" }}
      >
        {n}
      </span>
      {Icon && (
        <span className="h-8 w-8 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "#F4EFE6", color: "#FF7A1A" }}>
          <Icon className="h-4 w-4" />
        </span>
      )}
      <span className="text-[13px] text-[#1A1A1A] leading-relaxed flex-1">{children}</span>
    </li>
  );
}
